import { graphql, Link } from "gatsby";
import { withoutOldMockups } from "../util/oldMockups";
import Img from "gatsby-image";
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  DotGroup,
  Slide,
  Slider,
} from "pure-react-carousel";
import React from "react";
import styled from "styled-components";
import media from "styled-media-query";
import Footer from "../components/footer.js";
import Header from "../components/Header";
import Layout from "../components/layout";
import { HelmetDatoCms } from "gatsby-source-datocms";
import Text, { Grid } from "../components/Theme";

// "In a room" images, generated at build time by room-images.js.
import roomImages from "../data/room-images.json";

// Page title and meta description for each print, so search results and
// link previews show the print's own name instead of the generic "Overview".
const productPageTitle = (product) =>
  product.slug === "gift-card"
    ? `${product.title} | Overview`
    : `${product.title} Satellite Print | Overview`;

const productPageDescription = (product) => {
  const text = (product.caption || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) {
    return `${product.title}: a large-format satellite print from Overview, printed in New York City.`;
  }
  return text.length > 155 ? `${text.slice(0, 152).replace(/\s+\S*$/, "")}...` : text;
};

// Sizes like 12x18 get an inch mark; other options (e.g. gift card amounts) don't.
// Product data for Google (rich results and free Shopping listings):
// one offer per size, all in stock, free US shipping.
const productJsonLd = (product, sizes) => {
  const url = `https://www.over-view.com/shop/${product.slug}`;
  const prices = sizes.map((s) => s.price).filter((n) => n > 0);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${product.title} Satellite Print`,
    description: productPageDescription(product),
    image: withoutOldMockups(product.productimg).map((i) => `${String(i.url).split("?")[0]}?fm=jpg&fit=max&w=1600`),
    sku: product.slug,
    brand: { "@type": "Brand", name: "Overview" },
    manufacturer: { "@id": "https://www.over-view.com/#organization" },
    creator: { "@id": "https://www.over-view.com/#benjamin-grant" },
    category: "Fine art prints > Satellite and aerial photography",
    countryOfOrigin: { "@type": "Country", name: "United States" },
    url,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: Math.min(...prices).toFixed(2),
      highPrice: Math.max(...prices).toFixed(2),
      offerCount: prices.length,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@id": "https://www.over-view.com/#organization" },
      url,
      offers: sizes
        .filter((s) => s.price > 0)
        .map((s) => ({
          "@type": "Offer",
          name: `${product.title}, ${s.size}`,
          price: s.price.toFixed(2),
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
          url,
          shippingDetails: {
            "@type": "OfferShippingDetails",
            shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "USD" },
            shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
          },
        })),
    },
  };
};

const sizeLabel = (size) => (/\d\s*x\s*\d/i.test(size) ? `${size}"` : size);

const Controls = styled.div`
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 44px;
  position: absolute;
  transition: all 100ms ease-in;
  button {
    padding: 10px;
    background-color: transparent;

    transition: all 100ms ease-in;
    color: #fff;
    border-radius: 100px;
    &:hover {
      opacity: 1;
      transform: scale(1.01);
    }
    &:disabled {
      opacity: 0;
    }
    ${media.lessThan("large")`
            padding: 15px;
        `}
  }
  .carousel__back-button {
    font-size: 24px;
    position: absolute;
    left: 20px;
    ${media.lessThan("large")`
            left: 0px;
        `}

    &:hover {
      transform: scale(1.2);
    }
    &:active {
      top: 2px;
    }
  }
  .carousel__next-button {
    font-size: 24px;
    position: absolute;

    right: 20px;

    ${media.lessThan("large")`
            right: 0px;
        `}

    &:hover {
      transform: scale(1.2);
    }
    &:active {
      top: 2px;
    }
  }
  button:focus {
    outline: none;
    box-shadow: 0px 0 0px 0px #406f64;
  }
`;

const InRoom = styled.section`
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 40px 100px;

  ${media.lessThan("medium")`
    padding: 10px 20px 60px;
  `}

  img {
    display: block;
    width: 100%;
    height: auto;
    background: #eee;
  }

  p {
    margin: 14px 0 0;
    font-size: 14px;
    color: #555;
  }
`;

const ProductHero = styled.div`
  ${Grid};
  min-height: 70vh;

  .div-block-3 {
    grid-column: 2/7 !important;
    select {
      max-width: 200px;
    }
    button {
      background-color: #000;
      color: #fff;
      padding: 15px 60px;
      border: 2px solid black;
      margin-right: 12px;

      @media (max-width: 812px) {
        padding: 15px 30px;
        margin-right: 8px;
        margin-bottom: 12px;
      }
    }

    h1.headline {
      font-family: ovhead, sans-serif;
      font-weight: 300;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-size: clamp(26px, 3vw, 36px);
      line-height: 1.2;
      margin-bottom: 16px;
    }
  }
  .carousel {
    &:hover {
      button {
        background: rgba(0, 0, 0, 0.4);
      }
    }
    .carousel__inner-slide {
      padding: 5px;
      ${media.lessThan("large")`
                padding: 0px;
            `}

      .gatsby-image-wrapper {
        height: 100%;
        img {
          object-fit: contain !important;
        }
      }
    }
  }
`;

const ProductDetails = styled.div`
  ${Grid};
  align-items: start;
`;

const ProductDesc = styled.div`
  padding-top: 40px;
  h3 {
    margin-bottom: 40px;
  }
`;

const Shipping = styled.div`
  grid-column: 2/13;
  padding-top: 60px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 120px;
  h3 {
    margin-bottom: 40px;
  }
`;

// "Buy It Framed" button (links to the print on Level Frames). Styled to
// look like a framed piece: a thick dark border with a thin white inner
// edge, so it stands out next to "Buy Now" and hints at what it offers.
const InverseButton = styled.button`
  background-color: white !important;
  color: black !important;
  border: 5px solid #282729 !important;
  box-shadow: inset 0 0 0 3px #ffffff, inset 0 0 0 4px rgba(0, 0, 0, 0.18),
    0 6px 16px rgba(0, 0, 0, 0.12);
  font-weight: 600;
  transition: box-shadow 200ms ease, transform 200ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: inset 0 0 0 3px #ffffff, inset 0 0 0 4px rgba(0, 0, 0, 0.18),
      0 10px 22px rgba(0, 0, 0, 0.18);
  }
`;

class ProductTemplate extends React.Component {
  constructor(props) {
    super(props);

    const { data } = props;

    const sizeArray =
      typeof data.datoCmsProduct.sizePrice === "string" &&
      data.datoCmsProduct.sizePrice.split("|").map((sizeStr) => {
        let [size, price] = sizeStr.split("[");

        price = price.replace("]", "");
        price = price.replace("+", "");
        price = parseFloat(price);

        return {
          size,
          price,
        };
      });

    this.state = {
      sizes: sizeArray || [],
      selectedSize: "",
    };
  }

  getPriceFromSize = (size) => {
    const index = this.state.sizes.findIndex((szObj) => szObj.size === size);

    if (index !== -1) return this.state.sizes[index].price;
  };

  dollarFormat = (amount, options = {}) => {
    const { precision = 2, returnZero = true, dollarSign = true } = options;

    const num = Number(amount);
    if (!(num || returnZero)) return "$0.00";

    const floatNum = num.toFixed(precision);

    const formattedNum = `${dollarSign ? "$" : ""}${floatNum}`;
    return formattedNum;
  };

  openLink = (link) => {
    window.open(link, "_target");
  };

  render() {
    const { data } = this.props;

    const gallery = withoutOldMockups(data.datoCmsProduct.productimg);
    return (
      <Layout>
        <HelmetDatoCms>
          <title>{productPageTitle(data.datoCmsProduct)}</title>
          <meta
            name="description"
            content={productPageDescription(data.datoCmsProduct)}
          />
          <meta property="og:title" content={productPageTitle(data.datoCmsProduct)} />
          <meta
            property="og:description"
            content={productPageDescription(data.datoCmsProduct)}
          />
          <meta
            property="og:image"
            content={`${data.datoCmsProduct.productimg[0].url}?auto=format&fit=clip&w=1200`}
          />
          {this.state.sizes.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify(productJsonLd(data.datoCmsProduct, this.state.sizes))}
            </script>
          )}
        </HelmetDatoCms>
        <Header content_color="black" />

        <ProductHero>
          <div id="w-node-911e36131b53-4a37690d">
            <CarouselProvider
              naturalSlideWidth={100}
              naturalSlideHeight={
                // Match the print's own shape so there's no empty band
                // above and below a square or wide image.
                Math.round(
                  100 /
                    ((data.datoCmsProduct.productimg[0] &&
                      data.datoCmsProduct.productimg[0].fluid &&
                      data.datoCmsProduct.productimg[0].fluid.aspectRatio) ||
                      0.8)
                )
              }
              totalSlides={gallery.length}
              visibleSlides={1}
            >
              <Slider>
                {gallery.map((img, index) => (
                  <Slide index={index} key={index}>
                    <Img fluid={img.fluid} alt="" />
                  </Slide>
                ))}
              </Slider>
              <DotGroup></DotGroup>
              <Controls>
                <ButtonBack>&larr;</ButtonBack>
                <ButtonNext>&rarr;</ButtonNext>
              </Controls>
            </CarouselProvider>
          </div>

          <div id="w-node-8dcc4f1a6824-4a37690d" className="div-block-3">
            <h1 className="headline">{data.datoCmsProduct.title}</h1>
            <div className="caption">
              {this.state.selectedSize
                ? this.dollarFormat(
                    this.getPriceFromSize(this.state.selectedSize)
                  )
                : this.state.sizes.length
                ? `${this.state.sizes.length > 1 ? "From " : ""}${this.dollarFormat(
                    Math.min(...this.state.sizes.map((s) => s.price))
                  )}`
                : null}
            </div>

            <form className="w-commerce-commerceaddtocartform">
              <div role="group">
                <select
                  id="option-set-11fcf8ecb4e2188de73a0485889de4a3"
                  className="caption w-select"
                  required
                  onChange={(e) => {
                    let size = e.target.value;

                    this.setState({ selectedSize: size });
                  }}
                >
                  <option value="">Select Size</option>
                  {this.state.sizes.map((szObj) => (
                    <option
                      value={szObj.size}
                      key={szObj.size}
                    >{sizeLabel(szObj.size)}</option>
                  ))}
                </select>
              </div>
            </form>

            <button
              className="Product snipcart-add-item caption"
              data-item-image={`${data.datoCmsProduct.productimg[0].url}?fit=crop&w=200&h=200&dpr=2`}
              data-item-id={data.datoCmsProduct.slug}
              data-item-price={0}
              data-item-name={data.datoCmsProduct.title}
              data-item-url={`https://www.over-view.com/shop/${data.datoCmsProduct.slug}`}
              data-item-stackable="false"
              data-item-shippable="true"
              data-item-custom1-name="Size"
              data-item-custom1-value={this.state.selectedSize}
              data-item-custom1-options={data.datoCmsProduct.sizePrice}
              onClick={(e) => {
                if (!this.state.selectedSize) {
                  e.preventDefault();
                  e.stopPropagation();
                  alert("Please select a size before adding to cart.");
                }
              }}
            >
              Buy Now &rarr;
            </button>
            {data.datoCmsProduct.levelFramesUrl && (
              <InverseButton
                className="Product caption"
                onClick={() =>
                  this.openLink(data.datoCmsProduct.levelFramesUrl)
                }
              >
                Buy It Framed &rarr;
              </InverseButton>
            )}
            <ProductDesc>
              <Text caption>Description</Text>
              <Text small>{data.datoCmsProduct.caption}</Text>
              {!["gift-card", "2026-nyc-marathon"].includes(data.datoCmsProduct.slug) && (
                <Text small>
                  <em>Includes a caption card with the story behind the image.</em>
                </Text>
              )}
            </ProductDesc>
          </div>
        </ProductHero>

        {roomImages[data.datoCmsProduct.slug] && (
          <InRoom>
            <img
              src={roomImages[data.datoCmsProduct.slug].src}
              width={roomImages[data.datoCmsProduct.slug].width}
              height={roomImages[data.datoCmsProduct.slug].height}
              alt={`${data.datoCmsProduct.title} print framed and hanging in a room`}
              loading="lazy"
            />
            <p>
              Shown at {roomImages[data.datoCmsProduct.slug].size.replace(/x/i, " × ")}" in a {roomImages[data.datoCmsProduct.slug].frame || "black"} frame. Visualization.
            </p>
          </InRoom>
        )}
        <ProductDetails>
          <Shipping>
            <Text caption>SHIPPING & PRINTING INFO</Text>
            <Text small>
              Free shipping within the United States, arriving in about 7 to 10 business days.
              <br />
              All prints, framed and unframed, are made to order by Level Frames in New York.
              <br />
              International shipping is available for an additional fee.
              <br />
              Damaged or defective prints are replaced free.{" "}
              <Link to="/shipping-returns" style={{ textDecoration: "underline" }}>
                Shipping &amp; returns
              </Link>
              .
            </Text>
          </Shipping>
        </ProductDetails>

        <Footer />
      </Layout>
    );
  }
}

export const query = graphql`
  query singleProductQuery($slug: String!) {
    datoCmsProduct(slug: { eq: $slug }) {
      width
      title
      source
      slug
      ratio
      sizePrice
      originalId
      height
      caption
      sizePrice
      levelFramesUrl

      productimg {
        url
        fluid(
          maxWidth: 900
          imgixParams: { dpr: 1, w: "900", fit: "clip", auto: "compress" }
        ) {
          ...GatsbyDatoCmsFluid
        }
      }
    }
  }
`;

export default ProductTemplate;
